"use client";
import { useInquiryAuth } from "./use-inquiry-auth";
import { useInquiryMessage } from "./use-inquiry-message";
import { useInquiryPresentation } from "./use-inquiry-presentation";
import type { BuyerProfileData } from "@/components/listings/buyer-profile-fields";
import type { InquiryResponse } from "@/components/intake/inquiry-intake-fields";
import { useInquirySender } from "@/hooks/use-inquiry-sender";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { useListingAnalytics } from "@/hooks/use-listing-analytics";
import { usePendingInquiry } from "@/hooks/use-pending-inquiry";
import { api } from "@/lib/api-client";
import { mediaQuery } from "@/lib/media-query";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTimeout, useWindowSize } from "react-use";
import { useMediaQuery } from "usehooks-ts";
import {
  createInquirySubmit,
  type InquiryUser,
  type DialogStep,
} from "./submit-inquiry";
import type { ListingInquiryDialogProps } from "../listing-inquiry-dialog";

export function useListingInquiryDialog({
  listing,
  open,
  onOpenChange,
  onSent,
}: ListingInquiryDialogProps) {
  const t = useTranslations();
  const searchParams = useSearchParams();

  const { user, loaded, reloadUser, senderError, delegated, signedIn } =
    useInquirySender();
  const { trackContactClick } = useListingAnalytics();
  const { displayErrorToast, formatError } = useErrorFormatter();
  const { save: savePendingInquiry } = usePendingInquiry();

  const [step, setStep] = useState<DialogStep>("form");
  const {
    email,
    setEmail,
    otpKind,
    setOtpKind,
    emailForm,
    onEmailSubmit,
    checkEmailMutation,
    createAccountMutation,
    sendLoginOTPMutation,
  } = useInquiryAuth({ savePendingAndPersist, setStep });
  const [startedUnauthenticated, setStartedUnauthenticated] = useState(false);
  const {
    message,
    setMessage,
    messageRef,
    handleTextareaFocus,
    handleTextareaBlur,
  } = useInquiryMessage(listing);
  const [error, setError] = useState<string | null>(null);
  const [conversationUrl, setConversationUrl] = useState<string | null>(null);
  const isDesktop = useMediaQuery(mediaQuery.sm);

  const [inlineName, setInlineName] = useState("");
  const [inlinePhone, setInlinePhone] = useState("");
  const [buyerProfile, setBuyerProfile] = useState<BuyerProfileData>({});
  const [intakeResponses, setIntakeResponses] = useState<
    Record<string, InquiryResponse>
  >({});

  const apiUtils = api.useUtils();
  const { data: listingIntakeForm } =
    api.os.intakeForms.getListingInquiryForm.useQuery(
      { listingId: listing.id },
      { enabled: open },
    );
  const sendMessageMutation = api.listings.sendMessage.useMutation();
  const [isConfettiComplete] = useTimeout(5000);
  const { width, height } = useWindowSize();

  const isLoggedIn = loaded && signedIn;
  const isOwnerOrAgent =
    user?.id === listing.ownerId || user?.id === listing.sellerAgentId;

  const canInquireQuery = api.listings.canInquire.useQuery(
    { listingId: listing.id },
    {
      enabled: open && isLoggedIn && !isOwnerOrAgent,
    },
  );

  const needsName = isLoggedIn && !user?.name;
  const needsPhone = isLoggedIn && !user?.phone;
  const needsBuyerProfile =
    isLoggedIn &&
    !user?.isAgent &&
    !user?.profile?.buyerTimeline &&
    !user?.profile?.buyerExperience;

  const submitInquiry = createInquirySubmit({
    listing,
    user,
    inlineName,
    inlinePhone,
    buyerProfile,
    intakeResponses,
    messageRef,
    apiUtils,
    sendMessageMutation,
    trackContactClick,
    searchParams,
    t,
    setError,
    setStep,
    setConversationUrl: (url) => setConversationUrl(delegated ? null : url),
    onSent,
    displayErrorToast,
    formatError,
  });

  function handleFormSubmit() {
    if (!loaded || senderError) return;
    if (isLoggedIn) {
      submitInquiry();
    } else {
      setStartedUnauthenticated(true);
      setStep("auth_start");
    }
  }

  function handlePostLogin(loggedInUser: InquiryUser) {
    if (!loggedInUser.name || !loggedInUser.phone) {
      setShowProfileInStepper(true);
      if (!loggedInUser.isAgent) {
        setShowBuyerProfileInStepper(true);
      }
      setStep("auth_complete_profile");
      return;
    }
    if (!loggedInUser.isAgent) {
      const hasBuyerProfile =
        loggedInUser.profile?.buyerTimeline ||
        loggedInUser.profile?.buyerExperience;
      if (
        !hasBuyerProfile &&
        !buyerProfile.buyerTimeline &&
        !buyerProfile.buyerExperience
      ) {
        setShowBuyerProfileInStepper(true);
        setStep("buyer_profile");
        return;
      }
    }
    submitInquiry(loggedInUser);
  }

  function onProfileDone(updatedUser: InquiryUser) {
    handlePostLogin(updatedUser);
  }

  async function onOTPDone() {
    if (!user || !loaded) {
      await reloadUser();
    }
  }

  function savePendingAndPersist() {
    savePendingInquiry({
      type: "listing",
      listingId: listing.id,
      message: messageRef.current,
    });
  }

  useEffect(() => {
    if (
      user &&
      loaded &&
      step !== "form" &&
      step !== "success" &&
      step !== "submitting" &&
      step !== "buyer_profile"
    ) {
      handlePostLogin(user);
    }
  }, [user, loaded]);

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setStep("form");
      setEmail("");
      setOtpKind("CREATE_ACCOUNT");
      setStartedUnauthenticated(false);
      setShowProfileInStepper(false);
      setShowBuyerProfileInStepper(false);
      setError(null);
      setInlineName("");
      setInlinePhone("");
      setBuyerProfile({});
      setIntakeResponses({});
      emailForm.reset();
    }
  }

  function handleBack(e: React.MouseEvent) {
    e.preventDefault();
    setStep("form");
    setEmail("");
    emailForm.reset();
  }

  const isNonClosable = step === "auth_otp" || step === "submitting";

  const [showProfileInStepper, setShowProfileInStepper] = useState(false);
  const [showBuyerProfileInStepper, setShowBuyerProfileInStepper] =
    useState(false);

  const {
    getTitle,
    showBackButton,
    stepperSteps,
    stepperCurrentStep,
    showStepper,
  } = useInquiryPresentation({
    step,
    startedUnauthenticated,
    isLoggedIn,
    showProfileInStepper,
    showBuyerProfileInStepper,
  });

  const profileName = user?.name ?? user?.email ?? "Your profile";

  const brokerInquiryBlocked =
    isLoggedIn &&
    canInquireQuery.data?.allowed === false &&
    canInquireQuery.data.reason === "BROKER_BLOCK";

  return {
    listing,
    onOpenChange,
    t,
    user,
    loaded,
    senderError,
    delegated,
    step,
    email,
    otpKind,
    message,
    setMessage,
    error,
    conversationUrl,
    isDesktop,
    inlineName,
    setInlineName,
    inlinePhone,
    setInlinePhone,
    buyerProfile,
    setBuyerProfile,
    intakeResponses,
    setIntakeResponses,
    listingIntakeForm,
    sendMessageMutation,
    checkEmailMutation,
    createAccountMutation,
    sendLoginOTPMutation,
    emailForm,
    isConfettiComplete,
    width,
    height,
    isLoggedIn,
    isOwnerOrAgent,
    needsName,
    needsPhone,
    needsBuyerProfile,
    handleFormSubmit,
    onEmailSubmit,
    onOTPDone,
    onProfileDone,
    savePendingAndPersist,
    handleOpenChange,
    handleTextareaFocus,
    handleTextareaBlur,
    getTitle,
    showBackButton,
    handleBack,
    isNonClosable,
    stepperSteps,
    stepperCurrentStep,
    showStepper,
    profileName,
    brokerInquiryBlocked,
    submitInquiry,
  };
}
